import {
  Autocomplete,
  Card,
  CardHeader,
  CircularProgress,
  FormControl,
  FormHelperText,
  Grid,
  InputAdornment,
  TextField as MuiTextField,
  Stack,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { useMutation, useQuery } from "@tanstack/react-query";
// import DatePicker from 'components/Extras/DatePicker';
import { ArrowBack, ListAltTwoTone, UploadOutlined } from "@mui/icons-material";
import { Button as AntButton, App, Spin, Typography, Upload } from "antd";
import { Field, Formik } from "formik";
import moment from "moment";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import imageCompression from 'browser-image-compression';

import { useLocation, useNavigate, useParams } from "react-router-dom";
import SelectField from "~/components/Extras/SelectField";
import TextField from "~/components/Extras/TextField";
import MDBox from "~/components/MDBox";
import MDButton from "~/components/MDButton";
import DashboardLayout from "~/examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "~/examples/Navbars/DashboardNavbar";
import useImageCompression from "~/hooks/useImageCompression";
import {
  useCreatePlantMonitoring,
  useMonitoringCategory,
  usePlantDetails,
  useUpdatePlantMonitoring,
} from "~/queries/plantMasterQuery";
import {
  getPlantCategory,
  getPlantSuggestions,
} from "~/services/plant.service";
import { extractFileName, getImageUrl } from "~/utils/utils";
import {
  MAX_CHARACTER_LENGTH,
  debounce,
  excludeEmptyFields,
  formInitialValues,
  validationSchema,
} from "./helper";

const fieldMappings = {
  Plant_Location_Name: "barcodedtl.plot_mstr.loc_mstr.Loc_Name",
  Plant_Plot_Name: "barcodedtl.plot_mstr.Plot_Name",
  Plant_Scientific_Name: "barcodedtl.plant_mstr.Plant_Scientific_Name",
  BarCode_Auto_ID: "BarCode_Auto_ID",
  Latitude: "barcodedtl.Latitude",
  Longitude: "barcodedtl.Longitude",
  Altitude_meter: "barcodedtl.Altitude_meter",
};

const PlantSurvey = () => {
  // Material UI Hooks
  const theme = useTheme();
  const matchesXs = useMediaQuery(theme.breakpoints.down("md"));
  // React-router hooks
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  // Ant Hooks
  const { message } = App.useApp();
  // React hooks
  const [fileList, setFileList] = useState([]);
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [options, setOptions] = useState([]);
  const [description, setDescription] = useState("");
  const [disableFieldsDuringUpdate, setDisableFieldsDuringUpdate] =
    useState(false);
  const [submitButtonLabel, setSubmitButtonLabel] = useState("Submit");

  const formRef = useRef(null);
  // Custom hooks
  const { compressedFile, isCompressing } = useImageCompression(
    fileList[0]?.originFileObj,
    "Plant_Img",
    formRef
  );

  // React query hooks

  const plantDetailsMutation = usePlantDetails();
  const createPlantSurveyMutation = useCreatePlantMonitoring();
  const updateMonitoringMutation = useUpdatePlantMonitoring();
  const monitoringCategoryMutation = useMonitoringCategory();

  // Variables declarations
  const format = "DD/MM/YYYY";

  const { data: survivabilityList } = useQuery({
    queryKey: ["Survivability"],
    queryFn: () => getPlantCategory("Survivability"),
    staleTime: Infinity,
  });

  const { data, mutateAsync, isLoading, reset } = useMutation({
    mutationKey: ["plantSuggestions", searchQuery],
    mutationFn: (searchParams) => getPlantSuggestions(searchParams),
    onSuccess: (data) => {
      // Modify the data response here
      console.log(data);
      setOptions(
        data?.data?.map((item) => ({
          label: item.BarCode_Serial_No,
          value: item.BarCode_Serial_No,
        }))
      );
    },
  });

  // This will insure that it should update the monitoring type or not.
  const allowPlantMonitoringUpdate = useMemo(
    () =>
      location.state &&
      typeof location.state === "object" &&
      Object.prototype.hasOwnProperty.call(location.state, "name"),
    [location.state]
  );

  const setFieldValuesFromPlantDetails = (plantDetails, setFieldValue) => {
    Object.entries(fieldMappings).forEach(([fieldName, propertyPath]) => {
      const propertyValue = propertyPath
        .split(".")
        .reduce((obj, prop) => obj?.[prop], plantDetails);
      setFieldValue(fieldName, propertyValue);
    });
  };

  const handleAutocompleteChange = async (value, action = false) => {
    if (!value?.label) {
      setSearchQuery(plantDetailsMutation?.variables);
      // formRef.current.setFieldValue(fieldName.trim(), '');
    } else {
      // do something else

      const response = await plantDetailsMutation.mutateAsync(value.label);
      monitoringCategoryMutation.mutateAsync();
      console.log("Autocomplete", response);

      if (response.status) {
        try {
          const { setFieldValue } = formRef.current;
          // const plantDetails = replaceDotWithUnderscore(data?.data);
          const plantDetails = response?.data;

          setFieldValuesFromPlantDetails(plantDetails, setFieldValue);
        } catch (error) {
          console.log("error", error);
        }
      }
    }
  };

  const setFormattedDate = (fieldName, locationState) => {
    const fieldValue = locationState?.[fieldName];
    return fieldValue ? moment(fieldValue).format(format) : null;
  };

  const setFieldValuesFromLocationState = (
    fields,
    locationState,
    setFieldValue
  ) => {
    fields.forEach((fieldName) => {
      setFieldValue(fieldName, setFormattedDate(fieldName, locationState));
    });
  };

  const fetchImageAsFile = async (url, name) => {
    const response = await fetch(url);
    const data = await response.blob();
    const metadata = { type: data.type };
    return new File([data], name, metadata);
  };

  const convertUrlsToFiles = async (urls) => {
    return Promise.all(
      urls.map(async (imgUrl, index) => {
        const fileName = extractFileName(imgUrl);
        const file = await fetchImageAsFile(getImageUrl(imgUrl), fileName);
        console.log(file, "fileFetched")
        return {
          uid: index,
          name: fileName,
          status: "done",
          url: getImageUrl(imgUrl),
          originFileObj: file,
          size: file.size
        };
      })
    );
  };

  const renderForms = async () => {
    const response = await plantDetailsMutation.mutateAsync(params.id);
    if (response.status || formRef.current) {
      try {
        // eslint-disable-next-line no-unsafe-optional-chaining
        const { setFieldValue } = formRef?.current;
        // const plantDetails = replaceDotWithUnderscore(data?.data);
        const plantDetails = response?.data;

        // This will take care for rendering form value for creating monitoring
        if (allowPlantMonitoringUpdate) {
          setSubmitButtonLabel("Create");
          setFieldValuesFromPlantDetails(plantDetails, setFieldValue);
        }
        // This will take care of updating previous monitored value
        if (!allowPlantMonitoringUpdate) {
          console.log("We will render the form data for update 🤷‍♂️🤷‍♂️");
          console.log("Coming from the table", location.state);
          setDisableFieldsDuringUpdate(true);
          setSubmitButtonLabel("Update");

          setFieldValuesFromPlantDetails(plantDetails, setFieldValue);
          setDescription(location.state?.Monitoring_Remark ?? "");

          let rawData = {};
          rawData = { ...location.state };
          const items = [
            "Flower_Bud_Formation",
            "Open_Flower",
            "Fruit_Seed_Formation",
            "Fruit_Seed_Ripe",
            "Fruit_Seed_Fall",
            "Leaf_bud_formation",
            "Leaf_fall",
          ];
          items.forEach((item) => delete rawData[item]);
          for (const key in rawData) {
            if (Object.hasOwnProperty.call(rawData, key)) {
              if (key !== "Plant_Img") {
                const value = rawData[key];
                setFieldValue(key, value);
              }
            }
          }
          setFieldValuesFromLocationState(items, location.state, setFieldValue);
          // Setting Image file for preview
          if (location.state?.Plant_Img) {
            const imageUrls = location.state.Plant_Img.split(',');
            const files = await convertUrlsToFiles(imageUrls);
            setFileList(files);
            setFieldValue("Plant_Img", files.map(file => file.originFileObj));

          } else {
            setFileList([]);
          }
        }
      } catch (error) {
        console.log("error while parsing form data", error);
      }
    }
  };

  const handleSubmit = async (values, helpers) => {
    console.log(values, "submited Data")
    try {
      let payload = {};

      if (!allowPlantMonitoringUpdate && allowPlantMonitoringUpdate !== null) {
        payload = { ...values };
        excludeEmptyFields.forEach((field) => {
          if (payload[field] === "Invalid date") payload[field] = "null";

          if (payload[field] === null || payload[field] === "")
            payload[field] = "null";
        });

        const updateResponse = await updateMonitoringMutation.mutateAsync({
          ...payload,
          updateId: location.state?.PlantMonitr_Auto_ID,
          Plant_Img: payload.Plant_Img ?? false,
          Altitude_meter:
            payload.Altitude_meter === "null" ? "00" : payload.Altitude_meter,
        });
        console.log("WE ARE UPDATING", updateResponse);
        message.success("Plant survey updated successfully.");
        setTimeout(() => {
          navigate("/plant-monitoring/list", { state: params.id });
        }, 200);
      }

      if (allowPlantMonitoringUpdate || allowPlantMonitoringUpdate === null) {
        payload = {
          ...values,
          Altitude_meter:
            values.Altitude_meter === "null" ? "00" : values.Altitude_meter,
        };
        excludeEmptyFields.forEach((field) => {
          if (payload[field] === "") {
            delete payload[field];
          }
        });

        const response = await createPlantSurveyMutation.mutateAsync(payload);

        if (allowPlantMonitoringUpdate) {
          setTimeout(() => {
            navigate(-1, { replace: true });
            message.success("Plant survey updated successfully.");
          }, 200);
        } else {
          setTimeout(() => {
            navigate("/plant-monitoring/list", {
              state: plantDetailsMutation?.variables,
            });
            message.success("Plant survey created successfully.");
          }, 200);
        }
      }
    } catch (error) {
      console.log("error on submit", error);
      message.error(error?.response?.data?.message);
      // Resetting form if error
      // helpers.resetForm();
    }
  };

  const handleSuggestionInput = (searchText) => {
    if (searchText === "" || searchText.length < 4) return;
    setSearchQuery(searchText);
    mutateAsync(searchText);
  };

  const handlePlantationTypeChange = (values) => {
    [
      "Leaf_bud_formation",
      "Leaf_fall",
      "Flower_Bud_Formation",
      "Open_Flower",
      "Fruit_Seed_Formation",
      "Fruit_Seed_Ripe",
      "Fruit_Seed_Fall",
      "PlantGrowth_Flower_Count",
      "PlantGrowth_Fruit_Count",
      "PlantGrowth_Height",
      "PlantGrowth_Collar_Diameter",
      "Canopy_cover",
      "Moisture_content",
      "Survivability",
      // new changes - tag v3.0.1
      "Nitrogen",
      "Phosphate",
      "Potassium",
      "Soil_PH",
      "Soil_Temperature",
      "Soil_Moisture",
      "Electrical_Conductivity",
      // "Plant_Status",
    ].forEach((fieldName) => formRef.current?.setFieldValue(fieldName, ""));
  };

  const debounceHandlerForSuggestions = useCallback(
    debounce(handleSuggestionInput, 500),
    []
  );

  useEffect(() => {
    if (params.id) {
      handleSuggestionInput(params.id);
      renderForms();
      monitoringCategoryMutation.mutateAsync();
    }

    // return () => {
    //   second;
    // };
  }, [params?.id, location.state]);

  // Function to conditionally set the value prop for the DatePicker
  const getDatePickerValue = (field) => {
    if (field === null || field === "") {
      return null; // Don't pass the value prop if value is null
    }
    // Parse the date and return it in the desired format
    return moment(field, format);
  };

  const resetState = (helpers) => {
    setSearchQuery("");
    setOptions([]);
    reset();
    monitoringCategoryMutation.reset();
    setDisableFieldsDuringUpdate(false);
    if (helpers) helpers.resetForm();

    if (params?.id) navigate(-1);
  };

  const generateDatePickerProps = (label, name, fieldValue) => ({
    sx: { width: "100%" },
    name: name,
    label: label,
    format: format,
    disableFuture: true,
    minDate: fieldValue
      ? moment(fieldValue, format).subtract(7, "day")
      : moment().subtract(7, "day"),
  });
  const blobToFile = (blob, file) => {
    // Create a new File object from the blob
    return new File([blob], file.name, {
      type: file.type,
      lastModified: file.lastModified,
    });
  };

  const handleUploadChange = async (info) => {
    let newFileList = [...info.fileList];
    if (newFileList.length > 4) {
      newFileList = newFileList.slice(-4);
    }
    
    const Fileoptions = {
      maxSizeMB: 4,
      maxWidthOrHeight: 1920,
      useWebWorker: true
    };

    // Compress images larger than 4MB and keep the list
    const updatedFileList = await Promise.all(
      newFileList.map(async (file) => {
        if ((file.originFileObj || file).size > 4000000) { // Compress only if file size is more than 4MB
          console.log("compressing")
          const compressed = await imageCompression(file.originFileObj || file, Fileoptions);

          return {
            ...file,
            originFileObj: blobToFile(compressed, file.originFileObj || file),
            size: compressed.size,
            url: URL.createObjectURL(compressed),
            status: 'done', // Create preview URL for compressed file
          };
        }
        return file;
      })
    );

    const updatedTotalSize = updatedFileList.reduce((sum, file) => sum + file.size, 0);
    
    // Check if the updated total size exceeds 16MB after compression
    if (updatedTotalSize > 16000000) { // 16MB in bytes
      alert("Total size of all images must not exceed 16MB after compression.");
      return;
    }

    setFileList(updatedFileList);
    formRef.current.setFieldValue("Plant_Img", updatedFileList.map((file) => file.originFileObj));
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <Spin tip="Compressing Image..." spinning={isCompressing}>
        <Grid container spacing={3} justifyContent="center">
          <Grid item xs={12} lg={12}>
            <Card>
              <MDBox p={2}>
                <CardHeader
                  action={
                    <MDButton
                      // component={Link}
                      variant="contained"
                      color={"success"}
                      aria-label="navigation"
                      startIcon={params.id ? <ArrowBack /> : <ListAltTwoTone />}
                      onClick={() =>
                        navigate(params.id ? -1 : "/plant-monitoring/list", {
                          replace: true,
                        })
                      }
                    >
                      {params.id ? "Back" : "Monitoring List"}
                    </MDButton>
                  }
                  title="Plant Monitoring"
                />
                {/* <MDTypography variant="h5">Plant Survey</MDTypography> */}
              </MDBox>
              <MDBox px={4}>
                <Grid container columnSpacing={2}>
                  <Grid item lg={3} md={3} sm>
                    <Autocomplete
                      size="small"
                      fullWidth
                      disabled={disableFieldsDuringUpdate}
                      sx={{ width: matchesXs ? "100%" : "100%" }}
                      id="asynchronous-customerList"
                      open={open}
                      value={searchQuery}
                      onOpen={() => {
                        setOpen(true);
                      }}
                      onClose={() => {
                        setOpen(false);
                      }}
                      // isOptionEqualToValue={(option, value) => option.id === value.id}
                      // getOptionLabel={(option) => option.title}
                      options={options}
                      loading={isLoading}
                      onChange={(event, newValue) => {
                        handleAutocompleteChange(newValue, false);
                      }}
                      onInputChange={(event, value) =>
                        debounceHandlerForSuggestions(value)
                      }
                      renderInput={(params) => (
                        <MuiTextField
                          {...params}
                          id="asynchronous-plant-suggestions"
                          label="Search Here..."
                          // name="customerList"
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <React.Fragment>
                                {isLoading ? (
                                  <CircularProgress size={20} />
                                ) : null}
                                {params.InputProps.endAdornment}
                              </React.Fragment>
                            ),
                          }}
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </MDBox>
              <MDBox pt={2} px={4}>
                <Formik
                  initialValues={formInitialValues}
                  validationSchema={validationSchema}
                  onSubmit={handleSubmit}
                  validateOnChange
                  innerRef={formRef}
                >
                  {({
                    errors,
                    handleSubmit,
                    handleChange,
                    touched,
                    values,
                    setFieldValue,
                    handleReset,
                  }) => (
                    <form noValidate onSubmit={handleSubmit}>
                      <Grid container rowSpacing={2} columnSpacing={2}>
                        <Grid item lg={3} md={3} sm={3} xs={6}>
                          <TextField
                            readOnly
                            label="Plant Location Name"
                            name="Plant_Location_Name"
                          />
                        </Grid>
                        <Grid item lg={3} md={3} sm={3} xs={6}>
                          <TextField
                            readOnly
                            label="Plant Plot Name"
                            name="Plant_Plot_Name"
                          />
                        </Grid>
                        <Grid item lg={3} md={3} sm={3} xs={6}>
                          <TextField
                            readOnly
                            label="Plant Plantation Name"
                            name="Plant_Scientific_Name"
                          />
                        </Grid>
                        <Grid item lg={3} md={3} sm={3} xs={6}>
                          <SelectField
                            disabled={disableFieldsDuringUpdate}
                            name="Plantation_Monitoring_Type"
                            label="Select Monitoring Type"
                            options={monitoringCategoryMutation.data?.data?.map(
                              (item) => ({
                                value: item.Cat_Name,
                                label: item.Cat_Name,
                              })
                            )}
                            onChange={(e) => {
                              handleChange(e);
                              handlePlantationTypeChange(values);
                            }}
                          />
                        </Grid>

                        {values.Plantation_Monitoring_Type ===
                          "Qualitative Monitoring" ? (
                          <>
                            <Grid item lg={3} sm={3} xs={6}>
                              <LocalizationProvider dateAdapter={AdapterMoment}>
                                <DatePicker
                                  {...generateDatePickerProps(
                                    "Leaf Bud Formation",
                                    "Leaf_bud_formation",
                                    values?.Leaf_bud_formation
                                  )}
                                  onChange={(e) =>
                                    setFieldValue(
                                      "Leaf_bud_formation",
                                      moment(e).format(format)
                                    )
                                  }
                                  value={getDatePickerValue(
                                    values.Leaf_bud_formation
                                  )}
                                  slotProps={{
                                    textField: {
                                      helperText:
                                        touched.Leaf_bud_formation &&
                                        errors.Leaf_bud_formation,
                                      error:
                                        touched.Leaf_bud_formation &&
                                        errors.Leaf_bud_formation,
                                    },
                                  }}
                                  renderInput={(params) => (
                                    <MuiTextField
                                      {...params}
                                      value={getDatePickerValue(
                                        values.Leaf_bud_formation
                                      )}
                                      fullWidth
                                      onKeyDown={(e) => e.preventDefault()}
                                    />
                                  )}
                                />
                              </LocalizationProvider>
                            </Grid>
                            <Grid item lg={3} sm={3} xs={6}>
                              <LocalizationProvider dateAdapter={AdapterMoment}>
                                <DatePicker
                                  {...generateDatePickerProps(
                                    "Leaf Fall",
                                    "Leaf_fall",
                                    values.state?.Leaf_fall
                                  )}
                                  onError={(error, value) =>
                                    console.log(
                                      "ON ERROR FIRED 🔥",
                                      error,
                                      value
                                    )
                                  }
                                  onChange={(e) =>
                                    setFieldValue(
                                      "Leaf_fall",
                                      moment(e).format(format)
                                    )
                                  }
                                  value={getDatePickerValue(values.Leaf_fall)}
                                  slotProps={{
                                    textField: {
                                      helperText:
                                        touched.Leaf_fall && errors.Leaf_fall,
                                      error:
                                        touched.Leaf_fall && errors.Leaf_fall,
                                    },
                                  }}
                                  renderInput={(params) => (
                                    <MuiTextField
                                      {...params}
                                      fullWidth
                                      onKeyDown={(e) => e.preventDefault()}
                                    />
                                  )}
                                />
                              </LocalizationProvider>
                            </Grid>

                            <Grid item lg={3} sm={3} xs={6}>
                              <LocalizationProvider dateAdapter={AdapterMoment}>
                                <DatePicker
                                  {...generateDatePickerProps(
                                    "Flower Bud Formation",
                                    "Flower_Bud_Formation",
                                    values.Flower_Bud_Formation
                                  )}
                                  onChange={(e) =>
                                    setFieldValue(
                                      "Flower_Bud_Formation",
                                      moment(e).format(format)
                                    )
                                  }
                                  value={getDatePickerValue(
                                    values.Flower_Bud_Formation
                                  )}
                                  slotProps={{
                                    textField: {
                                      helperText:
                                        touched.Flower_Bud_Formation &&
                                        errors.Flower_Bud_Formation,
                                      error:
                                        touched.Flower_Bud_Formation &&
                                        errors.Flower_Bud_Formation,
                                    },
                                  }}
                                  renderInput={(params) => (
                                    <MuiTextField
                                      {...params}
                                      fullWidth
                                      onKeyDown={(e) => e.preventDefault()}
                                    />
                                  )}
                                />
                              </LocalizationProvider>
                            </Grid>
                            <Grid item lg={3} sm={3} xs={6}>
                              <LocalizationProvider dateAdapter={AdapterMoment}>
                                <DatePicker
                                  {...generateDatePickerProps(
                                    "Open Flower",
                                    "Open_Flower",
                                    values?.Open_Flower
                                  )}
                                  onChange={(e) =>
                                    setFieldValue(
                                      "Open_Flower",
                                      moment(e).format(format)
                                    )
                                  }
                                  value={getDatePickerValue(values.Open_Flower)}
                                  name="Open_Flower"
                                  slotProps={{
                                    textField: {
                                      helperText:
                                        touched.Open_Flower &&
                                        errors.Open_Flower,
                                      error:
                                        touched.Open_Flower &&
                                        errors.Open_Flower,
                                    },
                                  }}
                                  renderInput={(params) => (
                                    <MuiTextField
                                      {...params}
                                      fullWidth
                                      onKeyDown={(e) => e.preventDefault()}
                                    />
                                  )}
                                />
                              </LocalizationProvider>
                            </Grid>
                            <Grid item lg={3} sm={3} xs={6}>
                              <LocalizationProvider dateAdapter={AdapterMoment}>
                                <DatePicker
                                  {...generateDatePickerProps(
                                    "Fruit / Seed Formation",
                                    "Fruit_Seed_Formation",
                                    values?.Fruit_Seed_Formation
                                  )}
                                  onChange={(e) =>
                                    setFieldValue(
                                      "Fruit_Seed_Formation",
                                      moment(e).format(format)
                                    )
                                  }
                                  value={getDatePickerValue(
                                    values.Fruit_Seed_Formation
                                  )}
                                  slotProps={{
                                    textField: {
                                      helperText:
                                        touched.Fruit_Seed_Formation &&
                                        errors.Fruit_Seed_Formation,
                                      error:
                                        touched.Fruit_Seed_Formation &&
                                        errors.Fruit_Seed_Formation,
                                    },
                                  }}
                                  renderInput={(params) => (
                                    <MuiTextField
                                      {...params}
                                      onKeyDown={(e) => e.preventDefault()}
                                    />
                                  )}
                                />
                              </LocalizationProvider>
                            </Grid>
                            <Grid item lg={3} sm={3} xs={6}>
                              <LocalizationProvider dateAdapter={AdapterMoment}>
                                <DatePicker
                                  {...generateDatePickerProps(
                                    "Fruit / Seed Ripe",
                                    "Fruit_Seed_Ripe",
                                    values?.Fruit_Seed_Ripe
                                  )}
                                  onChange={(e) =>
                                    setFieldValue(
                                      "Fruit_Seed_Ripe",
                                      moment(e).format(format)
                                    )
                                  }
                                  value={getDatePickerValue(
                                    values.Fruit_Seed_Ripe
                                  )}
                                  slotProps={{
                                    textField: {
                                      helperText:
                                        touched.Fruit_Seed_Ripe &&
                                        errors.Fruit_Seed_Ripe,
                                      error:
                                        touched.Fruit_Seed_Ripe &&
                                        errors.Fruit_Seed_Ripe,
                                    },
                                  }}
                                  renderInput={(params) => (
                                    <MuiTextField
                                      {...params}
                                      fullWidth
                                      onKeyDown={(e) => e.preventDefault()}
                                    />
                                  )}
                                />
                              </LocalizationProvider>
                            </Grid>
                            <Grid item lg={3} sm={3} xs={6}>
                              <LocalizationProvider dateAdapter={AdapterMoment}>
                                <DatePicker
                                  {...generateDatePickerProps(
                                    "Fruit / Seed Fall",
                                    "Fruit_Seed_Fall",
                                    values?.Fruit_Seed_Fall
                                  )}
                                  onChange={(e) =>
                                    setFieldValue(
                                      "Fruit_Seed_Fall",
                                      moment(e).format(format)
                                    )
                                  }
                                  value={getDatePickerValue(
                                    values.Fruit_Seed_Fall
                                  )}
                                  slotProps={{
                                    textField: {
                                      helperText:
                                        touched.Fruit_Seed_Fall &&
                                        errors.Fruit_Seed_Fall,
                                      error:
                                        touched.Fruit_Seed_Fall &&
                                        errors.Fruit_Seed_Fall,
                                    },
                                  }}
                                  renderInput={(params) => (
                                    <MuiTextField
                                      {...params}
                                      fullWidth
                                      onKeyDown={(e) => e.preventDefault()}
                                    />
                                  )}
                                />
                              </LocalizationProvider>
                            </Grid>
                            <Grid item lg={3} sm={3} xs={6}>
                              <TextField
                                label="PlantGrowth Flower Count"
                                name="PlantGrowth_Flower_Count"
                                type="number"
                              />
                            </Grid>
                            <Grid item lg={3} sm={3} xs={6}>
                              <TextField
                                label="PlantGrowth Fruit Count"
                                name="PlantGrowth_Fruit_Count"
                                type="number"
                              />
                            </Grid>
                          </>
                        ) : null}

                        {values.Plantation_Monitoring_Type ===
                          "Quantitative Monitoring" ? (
                          <>
                            <Grid item lg={3} xl={3} sm={3} xs={6}>
                              <TextField
                                isRequired
                                label="Plant Height (m)"
                                name="PlantGrowth_Height"
                                type="number"
                              />
                            </Grid>
                            <Grid item lg={3} xl={3} sm={3} xs={6}>
                              <TextField
                                label="Plant Collar Diameter (cm)"
                                name="PlantGrowth_Collar_Diameter"
                                type="number"
                              />
                            </Grid>
                            <Grid item lg={3} xl={3} sm={3} xs={6}>
                              <TextField
                                label="Canopy Cover (cm)"
                                name="Canopy_cover"
                                type="number"
                              />
                            </Grid>
                            <Grid item lg={3} xl={3} sm={3} xs={6}>
                              <TextField
                                label="Moisture Content (%)"
                                name="Moisture_content"
                                type="number"
                              />
                            </Grid>
                            <Grid item lg={4} xl={3} sm={3} xs={6}>
                              <SelectField
                                label="Survivability"
                                name="Survivability"
                                options={survivabilityList.data?.map(
                                  (item) => ({
                                    value: item.Cat_Value,
                                    label: item.Cat_Value,
                                  })
                                )}
                                placeholder="Select Options"
                              />
                            </Grid>
                            {/* // new changes - tag v3.0.1 */}
                            <Grid item lg={3} xl={3} sm={3} xs={6}>
                              <TextField
                                label="Nitrogen (mg)"
                                name="Nitrogen"
                                type="number"
                                isRequired
                              />
                            </Grid>
                            <Grid item lg={3} xl={3} sm={3} xs={6}>
                              <TextField
                                label="Phosphate (mg)"
                                name="Phosphate"
                                type="number"
                                isRequired
                              />
                            </Grid>
                            <Grid item lg={3} xl={3} sm={3} xs={6}>
                              <TextField
                                label="Potassium (mg)"
                                name="Potassium"
                                type="number"
                                isRequired
                              />
                            </Grid>
                            <Grid item lg={3} xl={3} sm={3} xs={6}>
                              <TextField
                                label="Soil (ph)"
                                name="Soil_PH"
                                type="number"
                                isRequired
                              />
                            </Grid>
                            <Grid item lg={3} xl={3} sm={3} xs={6}>
                              <TextField
                                label="Soil Temperature (c)"
                                name="Soil_Temperature"
                                type="number"
                                isRequired
                              />
                            </Grid>
                            <Grid item lg={3} xl={3} sm={3} xs={6}>
                              <TextField
                                label="Soil Moisture (%)"
                                name="Soil_Moisture"
                                type="number"
                                isRequired
                              />
                            </Grid>
                            <Grid item lg={3} xl={3} sm={3} xs={6}>
                              <TextField
                                label="Electrical Conductivity (ds/cm)"
                                name="Electrical_Conductivity"
                                type="number"
                                isRequired
                              />
                            </Grid>
                          </>
                        ) : null}
                        {values.Plantation_Monitoring_Type !== "" ? (
                          <>
                            {/* <Grid item lg={3} xl={3} sm={3} xs={6}>
                              <SelectField
                                label="Plant Status"
                                name="Plant_Status"
                                options={plantStatus.map((item) => ({
                                  value: item.value,
                                  label: item.label,
                                }))}
                                placeholder="Select Options"
                                isRequired={true}
                              />
                            </Grid> */}
                            <Grid item lg={6} sm={6} xs={12}>
                              <TextField
                                label="Monitoring Remarks"
                                name="Monitoring_Remark"
                                onChange={(e) => {
                                  setDescription(e.target.value);
                                  formRef.current?.handleChange(e);
                                }}
                                inputProps={{ maxLength: MAX_CHARACTER_LENGTH }}
                                endAdornment={
                                  <InputAdornment position="end">
                                    <Typography.Text>{`${description?.length}/${MAX_CHARACTER_LENGTH}`}</Typography.Text>
                                  </InputAdornment>
                                }
                              />
                            </Grid>
                            <Grid item lg={6} sm={6}>
                              <FormControl>
                                <Field
                                  name="Plant_Img"
                                  render={({ field }) => (
                                    <Upload
                                      {...field}
                                      fileList={fileList}
                                      listType="picture"
                                      customRequest={({ file, onSuccess }) => {
                                        setTimeout(() => onSuccess("ok"), 100);
                                      }}
                                      maxCount={4}
                                      // multiple
                                      className="upload-list-inline"
                                      accept=".jpg,.png,.jpeg"
                                      onChange={handleUploadChange}
                                      onRemove={(file) => {
                                        const updatedFileList = fileList.filter(
                                          (item) => item.uid !== file.uid
                                        );
                                        setFileList(updatedFileList);
                                        setFieldValue("Plant_Img", updatedFileList.map((item) => item.originFileObj));
                                      }}
                                    >
                                      {fileList.length < 4 && (
                                        <AntButton icon={<UploadOutlined />}>Select File</AntButton>
                                      )}
                                    </Upload>
                                  )}
                                />
                                {touched.Plant_Img && errors.Plant_Img && (
                                  <FormHelperText
                                    mb="20px"
                                    color={"Red"}
                                    error={
                                      touched.Plant_Img &&
                                      Boolean(errors.Plant_Img)
                                    }
                                  >
                                    {errors.Plant_Img}
                                  </FormHelperText>
                                )}
                                <FormHelperText
                                  mb="20px"
                                  color={"Red"}
                                  warnin
                                >
                                  Please upload a minimum of 1 image and a maximum of 4 images (formats: .jpg, .png, .jpeg), with the total size of all images not exceeding 16MB and 4MB each.
                                </FormHelperText>
                              </FormControl>
                            </Grid>
                          </>
                        ) : null}

                        <Grid
                          container
                          my={2}
                          spacing={2}
                          justifyContent="end"
                          alignItems="center"
                        >
                          <Stack direction="row" spacing={2} useFlexGap>
                            <MDButton
                              sx={{ px: 5 }}
                              variant="contained"
                              color="error"
                              size={matchesXs ? "small" : "large"}
                              type="reset"
                              onClick={(e) => {
                                handleReset(e);
                                resetState(null);
                              }}
                            >
                              Cancel
                            </MDButton>

                            <MDButton
                              sx={{ px: 6 }}
                              type="submit"
                              fullWidth
                              variant="contained"
                              color="success"
                              size={matchesXs ? "small" : "large"}
                              disabled={createPlantSurveyMutation.isLoading}
                              onClick={() => {
                                console.log("ON SUBMIT", formRef.current);
                                Boolean(errors?.conditionalValidation) &&
                                  message.error(errors.conditionalValidation);
                              }}
                            >
                              {submitButtonLabel}
                            </MDButton>
                          </Stack>
                        </Grid>
                      </Grid>
                      {/* </Spin> */}
                    </form>
                  )}
                </Formik>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </Spin>
    </DashboardLayout>
  );
};

export default PlantSurvey;
